// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/pages/application/DocumentsPage.tsx
//
// Phase 1 Wave 12 Task 99 — Vendor application step 3 (document upload).
// Phase 7 Wave 18 Task 78 batch C6 (Amendment #1) — i18n rewrite.
//
// P0-2 (vendor-audit 2026-08-02): files now upload for real at select time
// via POST /api/v1/vendor/kyc/documents (multipart) and the returned doc_id
// is persisted on the store slot — the old Wave-12 stub kept only
// {name,size} metadata and the binary was silently discarded, so the
// application never carried any document. The insurance slot has no
// matching KYC doc_type enum value and stays metadata-only (optional).

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  VendorApplicationStepperLayout,
  cn,
} from '@printway/ui';

import { csrfFetch } from '../../lib/api/csrfFetch.js';
import { useTranslation } from '../../lib/i18n/index.js';
import {
  useApplicationStore,
  type Documents,
  type DocumentRef,
} from './ApplicationStore.js';

interface UploadSlot {
  slot: keyof Documents;
  labelKey: string;
  descriptionKey: string;
  required: boolean;
  accept: string;
}

/**
 * Slot → BE KYC doc_type. `null` = no matching enum value (VENDOR_KYC_DOC_TYPES
 * is business_license|id_card|tax_certificate|bank_proof) — slot stays local.
 */
const KYC_DOC_TYPE_BY_SLOT: Record<keyof Documents, string | null> = {
  businessLicense: 'business_license',
  taxId: 'tax_certificate',
  insuranceCertificate: null,
};

const SLOTS: UploadSlot[] = [
  {
    slot: 'businessLicense',
    labelKey: 'application.documents.businessLicense',
    descriptionKey: 'application.documents.businessLicenseDesc',
    required: true,
    accept: 'application/pdf,image/*',
  },
  {
    slot: 'taxId',
    labelKey: 'application.documents.taxId',
    descriptionKey: 'application.documents.taxIdDesc',
    required: true,
    accept: 'application/pdf,image/*',
  },
  {
    slot: 'insuranceCertificate',
    labelKey: 'application.documents.insurance',
    descriptionKey: 'application.documents.insuranceDesc',
    required: false,
    accept: 'application/pdf',
  },
];

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

interface UploadFieldProps {
  slot: UploadSlot;
  value: DocumentRef | null;
  uploading: boolean;
  onSelect: (file: File) => void;
  onRemove: () => void;
}

function UploadField({
  slot,
  value,
  uploading,
  onSelect,
  onRemove,
}: UploadFieldProps): JSX.Element {
  const { t } = useTranslation('onboarding');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0];
    if (f) onSelect(f);
    // Allow re-selecting the same file after a failed upload.
    e.target.value = '';
  };

  return (
    <div>
      <p className="text-sm font-medium text-pw-text">
        {t(slot.labelKey)}{slot.required ? ' *' : ''}
      </p>
      <p className="text-xs text-pw-text-muted">{t(slot.descriptionKey)}</p>
      {value ? (
        <div className="mt-2 flex items-center justify-between rounded-lg border border-pw-border bg-pw-surface px-4 py-3">
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className="h-5 w-5 text-pw-text-muted">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" />
            </svg>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-pw-text">{value.name}</p>
              <p className="text-xs text-pw-text-muted">{formatBytes(value.size)}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-pw-text-muted hover:text-pw-text"
          >
            {t('application.documents.remove')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className={cn(
            'mt-2 flex w-full flex-col items-center gap-1 rounded-lg border-2 border-dashed border-pw-border bg-pw-bg px-4 py-6 text-center transition-colors',
            'hover:border-pw-primary/40 hover:bg-pw-surface',
            uploading && 'cursor-wait opacity-60',
          )}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className="h-6 w-6 text-pw-text-muted">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span className="text-sm font-medium text-pw-text">
            {uploading
              ? t('application.documents.uploading')
              : t('application.documents.uploadCta')}
          </span>
          <span className="text-xs text-pw-text-muted">{t(slot.descriptionKey)}</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={slot.accept}
        onChange={handleSelect}
        className="hidden"
      />
    </div>
  );
}

export function DocumentsPage(): JSX.Element {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const documents = useApplicationStore((s) => s.data.documents);
  const setDocument = useApplicationStore((s) => s.setDocument);
  const [uploadingSlot, setUploadingSlot] = useState<keyof Documents | null>(
    null,
  );
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileSelected = async (
    slotKey: keyof Documents,
    file: File,
  ): Promise<void> => {
    setUploadError(null);
    const docType = KYC_DOC_TYPE_BY_SLOT[slotKey];
    if (!docType) {
      // No KYC doc_type for this slot (insurance) — metadata only, optional.
      setDocument(slotKey, {
        name: file.name,
        size: file.size,
        docId: null,
        docType: null,
      });
      return;
    }
    setUploadingSlot(slotKey);
    try {
      const fd = new FormData();
      // doc_type MUST precede the file part — the BE reads mp.fields off the
      // multipart stream in order.
      fd.append('doc_type', docType);
      fd.append('file', file);
      const res = await csrfFetch('/api/v1/vendor/kyc/documents', {
        method: 'POST',
        body: fd,
      });
      const json = (await res.json().catch(() => null)) as {
        ok?: boolean;
        data?: { doc_id?: string };
        error?: { message?: string; user_message?: string };
      } | null;
      if (!res.ok || !json?.ok || !json.data?.doc_id) {
        throw new Error(
          json?.error?.user_message ??
            json?.error?.message ??
            t('application.documents.uploadFailed'),
        );
      }
      setDocument(slotKey, {
        name: file.name,
        size: file.size,
        docId: json.data.doc_id,
        docType,
      });
    } catch (err) {
      setUploadError(
        err instanceof Error
          ? err.message
          : t('application.documents.uploadFailed'),
      );
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleRemove = (slotKey: keyof Documents): void => {
    const ref = documents[slotKey];
    if (ref?.docId) {
      // Best-effort server-side delete; the slot clears regardless and a
      // re-upload simply supersedes the orphaned doc in the admin queue.
      void csrfFetch(`/api/v1/vendor/kyc/documents/${ref.docId}`, {
        method: 'DELETE',
      }).catch(() => undefined);
    }
    setDocument(slotKey, null);
  };

  // Required slots must have really uploaded (docId) — metadata-only no
  // longer counts, that was the P0-2 data-loss stub.
  const canContinue =
    documents.businessLicense?.docId != null &&
    documents.taxId?.docId != null;

  return (
    <VendorApplicationStepperLayout currentStep={3}>
      <h2 className="text-xl font-semibold text-pw-text">{t('application.documents.heading')}</h2>
      <p className="mt-1 text-sm text-pw-text-muted">
        {t('application.documents.description')}
      </p>

      {uploadError ? (
        <p className="mt-3 rounded-md border border-pw-danger/40 bg-pw-danger/10 px-3 py-2 text-sm text-pw-danger" role="alert">
          {uploadError}
        </p>
      ) : null}

      <div className="mt-6 space-y-5">
        {SLOTS.map((s) => (
          <UploadField
            key={s.slot}
            slot={s}
            value={documents[s.slot]}
            uploading={uploadingSlot === s.slot}
            onSelect={(file) => void handleFileSelected(s.slot, file)}
            onRemove={() => handleRemove(s.slot)}
          />
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/application/store-setup')}
        >
          {t('application.documents.back')}
        </Button>
        <Button
          type="button"
          disabled={!canContinue}
          onClick={() => navigate('/application/bank-details')}
        >
          {t('application.documents.continue')}
        </Button>
      </div>
    </VendorApplicationStepperLayout>
  );
}

export default DocumentsPage;
